import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  TrendingDown,
  Users,
  MousePointer2,
  Download,
  MessageCircle,
  Phone,
  Target,
  Search,
  BarChart3,
  Clock,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  analyticsSummary,
  analyticsFunnel,
  analyticsSources,
  analyticsCampaigns,
  analyticsDaily,
} from "@/lib/admin-api";
import { toast } from "sonner";

const CHANNEL_LABELS = {
  google_ads: "Google Ads",
  meta_ads: "Meta Ads",
  organico: "Orgánico",
  directo: "Directo",
  otros: "Otros",
};

const CHANNEL_COLORS = {
  google_ads: "bg-red-600 text-white",
  meta_ads: "bg-blue-600 text-white",
  organico: "bg-emerald-600 text-white",
  directo: "bg-neutral-500 text-white",
  otros: "bg-amber-500 text-white",
};

function KPI({ icon: Icon, label, value, hint, color = "from-neutral-700 to-neutral-900" }) {
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-4 md:p-5" data-testid={`analytics-kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] text-neutral-500 font-medium uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-black tracking-tight text-neutral-900">{value}</p>
          {hint && <p className="text-[11px] text-neutral-400">{hint}</p>}
        </div>
      </div>
    </div>
  );
}

function FunnelStep({ step, maxValue, prevValue }) {
  const pct = maxValue > 0 ? Math.max(6, (step.value / maxValue) * 100) : 6;
  const dropPct = prevValue > 0 && step.value < prevValue
    ? Math.round(((prevValue - step.value) / prevValue) * 100)
    : null;
  return (
    <div className="space-y-1" data-testid={`funnel-step-${step.key}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold text-neutral-700">{step.label}</span>
        <span className="text-neutral-500">
          <strong className="text-neutral-900">{step.value}</strong>
          {step.conversion_from_top != null && (
            <span className="ml-2 text-neutral-400">({step.conversion_from_top}% del total)</span>
          )}
        </span>
      </div>
      <div className="h-8 bg-neutral-100 rounded-lg overflow-hidden relative">
        <div
          className="h-full bg-gradient-to-r from-red-500 to-orange-500 rounded-lg transition-all"
          style={{ width: `${pct}%` }}
        />
        {dropPct !== null && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-red-700 bg-white/90 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
            <TrendingDown className="w-3 h-3" /> -{dropPct}%
          </span>
        )}
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [sources, setSources] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [daily, setDaily] = useState(null);

  const load = async (d) => {
    setLoading(true);
    try {
      const [sm, fn, sr, cm, dl] = await Promise.all([
        analyticsSummary({ days: d }),
        analyticsFunnel({ days: d }),
        analyticsSources({ days: d }),
        analyticsCampaigns({ days: d, limit: 20 }),
        analyticsDaily({ days: d }),
      ]);
      setSummary(sm);
      setFunnel(fn);
      setSources(sr);
      setCampaigns(cm);
      setDaily(dl);
    } catch {
      toast.error("Error cargando analítica");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeDays = (d) => {
    setDays(d);
    load(d);
  };

  const maxFunnel = useMemo(() => {
    if (!funnel?.steps) return 0;
    return Math.max(...funnel.steps.map((s) => s.value), 1);
  }, [funnel]);

  const hasNoData = !loading && summary && summary.total_events === 0;

  return (
    <div className="p-5 md:p-8 max-w-7xl" data-testid="admin-analytics-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h1 className="section-title text-3xl md:text-4xl font-black flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-red-600" /> Analítica
          </h1>
          <p className="text-neutral-500 text-sm mt-1">
            Desempeño de la landing y campañas (independiente de GA4).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-neutral-500 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> Rango:
          </label>
          <select
            value={days}
            onChange={(e) => onChangeDays(parseInt(e.target.value, 10))}
            className="form-input text-sm"
            data-testid="analytics-range-select"
          >
            <option value={7}>Últimos 7 días</option>
            <option value={14}>Últimos 14 días</option>
            <option value={30}>Últimos 30 días</option>
            <option value={60}>Últimos 60 días</option>
            <option value={90}>Últimos 90 días</option>
          </select>
        </div>
      </div>

      {hasNoData && (
        <div className="mb-5 rounded-2xl border border-orange-200 bg-orange-50 p-4 text-sm text-orange-800">
          <strong>Aún no hay eventos registrados.</strong> El tracker se dispara cuando los visitantes
          entran a la landing o convierten. A medida que llegue tráfico aparecerán datos aquí.
        </div>
      )}

      {/* KPIs */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KPI icon={Users} label="Visitas" value={summary.page_views} hint={`${summary.unique_visitors} únicos`} color="from-red-500 to-red-600" />
          <KPI icon={Target} label="Leads" value={summary.leads_created} hint={`CVR ${summary.conversion_rate}%`} color="from-orange-500 to-orange-600" />
          <KPI icon={Download} label="Catálogo" value={summary.catalog_downloads} hint="Descargas" color="from-purple-500 to-purple-600" />
          <KPI icon={MessageCircle} label="WhatsApp" value={summary.whatsapp_clicks} hint={`${summary.phone_clicks} llamadas`} color="from-emerald-500 to-green-600" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        {/* Funnel */}
        {funnel && (
          <div className="lg:col-span-1 bg-white rounded-2xl border border-neutral-200 p-5" data-testid="analytics-funnel">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-base">Funnel de conversión</h3>
                <p className="text-xs text-neutral-500">{funnel.range?.start} → {funnel.range?.end}</p>
              </div>
            </div>
            <div className="space-y-3">
              {funnel.steps.map((s, idx) => (
                <FunnelStep
                  key={s.key}
                  step={s}
                  maxValue={maxFunnel}
                  prevValue={idx > 0 ? funnel.steps[idx - 1].value : 0}
                />
              ))}
            </div>
          </div>
        )}

        {/* Daily chart */}
        {daily && (
          <div className="lg:col-span-2 bg-white rounded-2xl border border-neutral-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-bold text-base">Visitas y conversión por día</h3>
                <p className="text-xs text-neutral-500">{daily.range?.start} → {daily.range?.end}</p>
              </div>
            </div>
            <div className="min-w-0" style={{ width: "100%", height: 280, minHeight: 280 }}>
              <ResponsiveContainer width="100%" height="100%" minWidth={240} minHeight={240}>
                <LineChart data={daily.series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="day" tickFormatter={(d) => (d || "").slice(5)} fontSize={11} stroke="#888" />
                  <YAxis allowDecimals={false} fontSize={11} stroke="#888" />
                  <Tooltip />
                  <Legend iconType="circle" />
                  <Line type="monotone" dataKey="visits" name="Visitas" stroke="#DC2626" strokeWidth={2.5} dot={{ r: 2 }} />
                  <Line type="monotone" dataKey="leads" name="Leads" stroke="#F97316" strokeWidth={2.5} dot={{ r: 2 }} />
                  <Line type="monotone" dataKey="catalog_downloads" name="Catálogo" stroke="#8B5CF6" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="whatsapp_clicks" name="WhatsApp" stroke="#10B981" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Sources */}
      {sources && (
        <div className="bg-white rounded-2xl border border-neutral-200 p-5 mb-6" data-testid="analytics-sources">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-base">Canales de tráfico</h3>
              <p className="text-xs text-neutral-500">Visitas, leads y tasa de conversión por canal</p>
            </div>
          </div>
          {(sources.channels || []).length === 0 ? (
            <p className="text-sm text-neutral-400 py-6 text-center">
              Sin datos en este rango.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-neutral-500 uppercase tracking-wider border-b border-neutral-200">
                  <tr>
                    <th className="text-left py-2 px-2">Canal</th>
                    <th className="text-right py-2 px-2">Visitas</th>
                    <th className="text-right py-2 px-2">Únicos</th>
                    <th className="text-right py-2 px-2">Leads</th>
                    <th className="text-right py-2 px-2">Catálogo</th>
                    <th className="text-right py-2 px-2">WhatsApp</th>
                    <th className="text-right py-2 px-2">Tel</th>
                    <th className="text-right py-2 px-2">CVR %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {sources.channels.map((c) => (
                    <tr key={c.channel} data-testid={`analytics-source-${c.channel}`}>
                      <td className="py-2.5 px-2">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-bold ${CHANNEL_COLORS[c.channel] || "bg-neutral-400 text-white"}`}>
                          {CHANNEL_LABELS[c.channel] || c.channel}
                        </span>
                      </td>
                      <td className="text-right font-semibold">{c.visits}</td>
                      <td className="text-right text-neutral-500">{c.unique_visitors}</td>
                      <td className="text-right font-semibold text-orange-600">{c.leads}</td>
                      <td className="text-right text-neutral-600">{c.catalog_downloads}</td>
                      <td className="text-right text-neutral-600">{c.whatsapp_clicks}</td>
                      <td className="text-right text-neutral-600">{c.phone_clicks}</td>
                      <td className="text-right font-bold">
                        <span className={c.conversion_rate >= 8 ? "text-emerald-600" : c.conversion_rate >= 3 ? "text-orange-600" : "text-neutral-500"}>
                          {c.conversion_rate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Campaigns + Keywords */}
      {campaigns && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
          <div className="bg-white rounded-2xl border border-neutral-200 p-5" data-testid="analytics-campaigns">
            <div className="flex items-center gap-2 mb-4">
              <MousePointer2 className="w-4 h-4 text-red-600" />
              <h3 className="font-bold text-base">Campañas (utm_campaign)</h3>
            </div>
            {(campaigns.campaigns || []).length === 0 ? (
              <p className="text-sm text-neutral-400 py-6 text-center">Aún no hay campañas registradas.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-neutral-500 uppercase tracking-wider border-b border-neutral-200">
                    <tr>
                      <th className="text-left py-2">Campaña</th>
                      <th className="text-right py-2">Visitas</th>
                      <th className="text-right py-2">Leads</th>
                      <th className="text-right py-2">CVR</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {campaigns.campaigns.map((c) => (
                      <tr key={c.campaign}>
                        <td className="py-2">
                          <p className="font-semibold text-neutral-800 truncate max-w-[220px]">{c.campaign}</p>
                          <p className="text-[10px] text-neutral-400">{c.source}</p>
                        </td>
                        <td className="text-right font-semibold">{c.visits}</td>
                        <td className="text-right font-semibold text-orange-600">{c.leads}</td>
                        <td className="text-right font-bold text-neutral-700">{c.conversion_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-neutral-200 p-5" data-testid="analytics-keywords">
            <div className="flex items-center gap-2 mb-4">
              <Search className="w-4 h-4 text-red-600" />
              <h3 className="font-bold text-base">Keywords (utm_term)</h3>
            </div>
            {(campaigns.keywords || []).length === 0 ? (
              <p className="text-sm text-neutral-400 py-6 text-center">
                Aún no hay keywords registrados.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-neutral-500 uppercase tracking-wider border-b border-neutral-200">
                    <tr>
                      <th className="text-left py-2">Keyword</th>
                      <th className="text-right py-2">Visitas</th>
                      <th className="text-right py-2">Leads</th>
                      <th className="text-right py-2">CVR</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {campaigns.keywords.map((k) => (
                      <tr key={k.term}>
                        <td className="py-2 truncate max-w-[260px] font-mono text-[12px] text-neutral-800">{k.term}</td>
                        <td className="text-right font-semibold">{k.visits}</td>
                        <td className="text-right font-semibold text-orange-600">{k.leads}</td>
                        <td className="text-right font-bold text-neutral-700">{k.conversion_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Integración nota */}
      <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4 text-xs text-neutral-600 flex items-start gap-2">
        <Activity className="w-4 h-4 flex-shrink-0 mt-0.5 text-neutral-500" />
        <span>
          Este reporte usa nuestro tracker propio (<code>/api/events</code>), independiente de Google Analytics. Los datos
          se generan con <em>page_view</em>, <em>lead_submit</em>, <em>catalog_download</em>, <em>whatsapp_click</em> y <em>phone_click</em>. Los leads creados vía formulario y catálogo emiten eventos server-side (100% confiable,
          no bloqueables por ad-blockers).
        </span>
      </div>
    </div>
  );
}
